@let roles = parent.myRoles$ | async;

<div class="w-full mx-auto mt-2 grid grid-cols-4 grid-rows-1 gap-4">

    <div
        class="col-span-full sm:col-span-3 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:border-slate-300 hover:shadow-md transition-all duration-200 flex flex-col gap-6">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 text-blue-500 text-lg">
                    <i class="fas fa-cog"></i>
                </div>
                <div class="flex flex-col">
                    <h3 class="text-lg font-bold text-slate-800 m-0 leading-tight">Quiz Settings</h3>
                    <p class="text-xs text-slate-400 mt-0.5">Configuration and rules for this quiz session</p>
                </div>
            </div>
            <div class="relative inline-block text-left" [class.cursor-pointer]="isStatusChangeable(roles)"
                (click)="toggleStatusDropdown($event, roles)">
                <app-chip
                    [label]="((parent.quiz?.quizStatus === quizStatus.inProgress ? 'In Progress' : parent.quiz?.quizStatus) ?? '') | titlecase"
                    [color]="
                        parent.quiz?.quizStatus === quizStatus.scheduled ? chipColor.SUCCESS :
                        parent.quiz?.quizStatus === quizStatus.draft ? chipColor.WARNING :
                        parent.quiz?.quizStatus === quizStatus.completed ? chipColor.INFO :
                        parent.quiz?.quizStatus === quizStatus.inProgress ? chipColor.DARK :
                        parent.quiz?.quizStatus === quizStatus.cancelled ? chipColor.DANGER : chipColor.WARNING
                    " [prefixIcon]="isStatusChangeable(roles) ? 'fas fa-chevron-down' : ''"></app-chip>

                @if (showStatusDropdown() && isStatusChangeable(roles)) {
                <div
                    class="absolute right-0 mt-2 z-50 w-44 origin-top-right rounded-lg bg-white border border-slate-200 shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none p-1 text-left">
                    <!-- Option 1: Close Quiz (Complete) -->
                    @if (parent.quiz?.quizStatus !== quizStatus.completed) {
                    <a (click)="onCompleteQuiz($event)"
                        class="hover:bg-green-100 cursor-pointer active:border-none text-sm py-2 px-4 font-normal block w-full whitespace-nowrap bg-transparent">
                        <i class="fa fa-circle-check text-emerald-500 mr-2 leading-relaxed"></i>
                        Close Quiz
                    </a>
                    }

                    <!-- Option 2: Cancel Quiz -->
                    @if (parent.quiz?.quizStatus !== quizStatus.cancelled) {
                    <a (click)="onCancelQuiz($event)"
                        class="hover:bg-amber-200 cursor-pointer active:border-none text-sm py-2 px-4 font-normal block w-full whitespace-nowrap bg-transparent">
                        <i class="fa fa-ban mr-2 text-amber-500 leading-relaxed"></i>
                        Cancel Quiz
                    </a>
                    }
                </div>
                }
            </div>
        </div>

        <div class="flex flex-row flex-wrap gap-4">
            <!-- Column 1: Timeline -->
            <div class="flex flex-col gap-3 flex-1">
                <h4
                    class="text-sm font-bold tracking-wide text-slate-500 flex items-center gap-2 pb-1.5 border-b-2 border-slate-100 m-0">
                    <i class="far fa-calendar-alt text-blue-500 text-sm"></i> Timeline
                </h4>
                <div class="flex flex-col gap-3.5 bg-slate-50/50 border border-slate-100 rounded-xl p-4 h-full">
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Start Date</span>
                        <app-chip [label]="((parent.quiz?.scheduledAt | moment: 'MMMM Do YYYY') ?? '') | titlecase"
                            [color]="chipColor.LIGHT"></app-chip>
                    </div>
                    <div class="flex items-center justify-between gap-4 border-t border-slate-100 pt-2 mt-1">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">End Date</span>
                        <app-chip [label]="((parent.quiz?.endDate | moment: 'MMMM Do YYYY') ?? '') | titlecase"
                            [color]="chipColor.LIGHT"></app-chip>
                    </div>
                </div>
            </div>

            <!-- Column 2: Access Control -->
            <div class="flex flex-col gap-3 flex-1">
                <h4
                    class="text-sm font-bold tracking-wide text-slate-500 flex items-center gap-2 pb-1.5 border-b-2 border-slate-100 m-0">
                    <i class="fas fa-lock text-blue-500 text-sm"></i> Access Control
                </h4>
                <div class="flex flex-col gap-3.5 bg-slate-50/50 border border-slate-100 rounded-xl p-4 h-full">
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Quiz Access</span>
                        <app-chip [label]="(parent.quiz?.byInvite ? 'By Invite' : 'Open For All') | titlecase"
                            [color]="parent.quiz?.byInvite ? chipColor.PRIMARY : chipColor.SUCCESS"></app-chip>
                    </div>
                    @if (parent.quiz?.byInvite && parent.quiz?.inviteCode && (roles?.management || (roles?.faculty &&
                    parent.quiz?.createdById === parent.user?.uid))) {
                    <div class="flex items-center justify-between gap-4 border-t border-slate-100 pt-2 mt-1">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Room Code</span>
                        <div class="flex items-center gap-1">
                            <app-chip class="font-mono" [label]="parent.quiz?.inviteCode ?? ''"
                                [color]="chipColor.PRIMARY"></app-chip>
                            <button (click)="parent.copyInviteCode()"
                                class="bg-transparent border-0 p-1 text-indigo-600 cursor-pointer hover:bg-slate-200 active:scale-95 transition-all duration-150 rounded-md inline-flex items-center justify-center"
                                title="Copy Invite Code">
                                <i class="far fa-copy text-xs"></i>
                            </button>
                        </div>
                    </div>
                    }
                </div>
            </div>

            <!-- Column 3: Rules & Evaluation -->
            <div class="flex flex-col gap-3 flex-1">
                <h4
                    class="text-sm font-bold tracking-wide text-slate-500 flex items-center gap-2 pb-1.5 border-b-2 border-slate-100 m-0">
                    <i class="fas fa-sliders-h text-blue-500 text-sm"></i> Rules & Evaluation
                </h4>
                <div class="flex flex-col gap-3.5 bg-slate-50/50 border border-slate-100 rounded-xl p-4 h-full">
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Multiple Attempts</span>
                        <app-chip [label]="(parent.quiz?.allowMultipleAttempts ? 'Allowed' : 'One-Time') | titlecase"
                            [color]="parent.quiz?.allowMultipleAttempts ? chipColor.SUCCESS : chipColor.DANGER"></app-chip>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Scoring Visibility</span>
                        <app-chip [label]="(parent.quiz?.enableScoring !== false ? 'Visible' : 'Hidden') | titlecase"
                            [color]="parent.quiz?.enableScoring !== false ? chipColor.SUCCESS : chipColor.WARNING"></app-chip>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Time Limit</span>
                        <app-chip
                            [label]="(parent.quiz?.enableTimeLimit !== false ? (parent.quiz?.totalDuration + ' min') : 'None') | titlecase"
                            [color]="parent.quiz?.enableTimeLimit !== false ? chipColor.WARNING : chipColor.LIGHT"></app-chip>
                    </div>
                    <div class="flex items-center justify-between gap-4 border-t border-slate-100 pt-2 mt-1">
                        <span class="text-xs font-semibold text-slate-500 tracking-wide">Match Threshold</span>
                        <app-chip [label]="((parent.quiz?.shortAnswerPercentage ?? 50) + '%') | titlecase"
                            [color]="chipColor.LIGHT"></app-chip>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="col-span-full sm:col-span-1 w-full h-full flex justify-between">
        <div class="grid grid-cols-3 grid-rows-1 sm:grid-cols-1 sm:grid-rows-3 gap-4 text-center w-full items-center">
            <div class="flex flex-col justify-center p-4 border border-gray-200 bg-white rounded-lg h-full w-full">
                <p class="text-2xl font-bold text-primary-500">{{ (parent.quiz?.questions)?.length || 0 }}</p>
                <p class="text-xs text-slate-500">Questions</p>
            </div>
            <div class="flex flex-col justify-center p-4 border border-gray-200 bg-white rounded-lg h-full w-full">
                <p class="text-2xl font-bold text-primary-500">{{ parent.quiz?.totalScore }}</p>
                <p class="text-xs text-slate-500">Marks</p>
            </div>
            <div class="flex flex-col justify-center p-4 border border-gray-200 bg-white rounded-lg h-full w-full">
                <p class="text-2xl font-bold text-red-500">
                    {{ parent.quiz?.enableTimeLimit !== false ? parent.quiz?.totalDuration + ' m' : 'No Limit' }}
                </p>
                <p class="text-xs text-slate-500">Duration</p>
            </div>
        </div>
    </div>

</div>

@if (roles?.management || (roles?.faculty && parent.quiz?.createdById === parent.user?.uid)) {
<div class="flex flex-col gap-4 mt-4 w-full">
    <div class="flex flex-col w-full bg-white border border-gray-200 rounded-lg p-4">
        <div class="flex flex-row justify-between">
            <span class="text-sm font-semibold text-primary-400">Selected Questions</span>
        </div>

        <div class="flex-1 overflow-y-auto touch-none flex flex-col gap-2 mt-4">
            @for (item of selectedQuestions(); track item.id; let i = $index) {
            <div class="flex flex-row items-start gap-2">
                <span class="text-sm text-gray-500 w-8 text-right py-2"># {{i + 1}}</span>
                <div
                    class="flex flex-col gap-1 w-full p-2 rounded-md touch-none shadow-sm border bg-white border-gray-200">
                    <span class="text-sm font-medium text-gray-700">{{ item.questionText }}</span>
                    <div class="flex flex-row gap-2">
                        <app-chip [label]="item.questionType | titlecase" [color]="chipColor.PRIMARY"></app-chip>
                        <app-chip [label]="(item.score + ' marks') | titlecase" [color]="chipColor.LIGHT"></app-chip>
                        <app-chip
                            [label]="(item.reference.book + ' ' + item.reference.chapter + (item.reference.verse ? (':' + item.reference.verse) : '')) | titlecase"
                            [color]="chipColor.LIGHT"></app-chip>
                    </div>
                </div>
            </div>
            }
        </div>
    </div>
    @if ((parent.quiz?.questions ?? []).length > selectedQuestions().length) {
    <div class="flex flex-row w-full items-center justify-center">
        <app-btn-more label="Show More" (click)="loadQuestions()"></app-btn-more>
    </div>
    }
</div>
}

@let group = parent.group$ | async;

@if (group?.memberStatus === membershipStatus.active) {
@let attempts = attempts$ | async;
<div class="col-span-full row-span-2 flex flex-col justify-start mt-4 gap-2">

    @if (parent.quiz?.quizStatus === quizStatus.completed && participantScore()) {
    @let scoreData = participantScore();

    <!-- Participant Score Card -->
    <span class="flex w-full items-center text-start text-md font-semibold text-primary-400 mt-2">Your Results</span>
    <div class="flex flex-row flex-wrap gap-4 mt-2">
        <!-- KPI 1: Score -->
        <div
            class="flex-1 min-w-[200px] bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div class="flex items-center gap-3 mb-2">
                <span class="text-sm font-semibold text-slate-500">Your Score</span>
            </div>
            <div class="text-2xl text-right font-bold text-indigo-600">{{ scoreData.score }} <span
                    class="text-sm font-normal text-slate-400">/ {{ parent.quiz?.totalScore }}</span></div>
            <div class="text-xs text-right text-slate-400 mt-1">Accuracy: {{ scoreData.percentage }}%</div>
        </div>
        <!-- KPI 2: Correct -->
        <div
            class="flex-1 min-w-[200px] bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div class="flex items-center gap-3 mb-2">
                <span class="text-sm font-semibold text-slate-500">Correct</span>
            </div>
            <div class="text-2xl text-right font-bold text-emerald-600">{{ scoreData.correctCount }}</div>
            <div class="text-xs text-right text-slate-400 mt-1">Answered correctly</div>
        </div>
        <!-- KPI 3: Incorrect -->
        <div
            class="flex-1 min-w-[200px] bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div class="flex items-center gap-3 mb-2">
                <span class="text-sm font-semibold text-slate-500">Incorrect</span>
            </div>
            <div class="text-2xl text-right font-bold text-rose-600">{{ scoreData.wrongCount }}</div>
            <div class="text-xs text-right text-slate-400 mt-1">Answered incorrectly</div>
        </div>
        <!-- KPI 4: Unanswered -->
        <div
            class="flex-1 min-w-[200px] bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div class="flex items-center gap-3 mb-2">
                <span class="text-sm font-semibold text-slate-500">Unanswered</span>
            </div>
            <div class="text-2xl text-right font-bold text-slate-600">{{ scoreData.unansweredCount }}</div>
            <div class="text-xs text-right text-slate-400 mt-1">Skipped/Unanswered</div>
        </div>
    </div>

    <!-- Performance Matrix Card -->
    <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mt-4 w-full max-w-full overflow-hidden">
        <h3 class="text-sm font-bold text-slate-800 mb-1.5 flex items-center gap-3">
            <span>Your Performance Matrix</span>
        </h3>
        <p class="text-[11px] text-slate-400 m-0 mb-4">Detailed breakdown of your responses per question.</p>

        <div class="overflow-x-auto w-full max-w-full">
            <table class="w-full text-left border-collapse min-w-full">
                <thead>
                    <tr class="border-b border-slate-100 bg-slate-50">
                        <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-8">#</th>
                        <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Question
                            Reference & Text</th>
                        <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Your Answer
                        </th>
                        <th class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Correct Answer
                        </th>
                        <th
                            class="py-2.5 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center w-20">
                            Result</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                    @for (ans of participantAnswers(); track ans.id; let i = $index) {
                    <tr class="hover:bg-slate-50/50 transition-colors">
                        <td class="py-2.5 px-3 text-xs font-semibold text-slate-400">{{ i + 1 }}</td>
                        <td class="py-2.5 px-3">
                            <div class="flex flex-col">
                                <span class="text-[10px] font-bold text-indigo-600 uppercase">
                                    @if (ans.reference?.book) { {{ ans.reference.book }} {{ ans.reference.chapter }}{{
                                    ans.reference.verse ? (':' + ans.reference.verse) : '' }} }
                                </span>
                                <span class="text-xs text-slate-700 font-medium line-clamp-2 mt-0.5">
                                    {{ ans.questionText }}
                                </span>
                            </div>
                        </td>
                        <td class="py-2.5 px-3 text-xs text-slate-600">{{ ans.userAnswer }}</td>
                        <td class="py-2.5 px-3 text-xs text-slate-600">{{ getCorrectAnswer(ans) }}</td>
                        <td class="py-2.5 px-3 text-center">
                            @if (ans.isCorrect) {
                            <span
                                class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 font-bold text-xs"
                                title="Correct">
                                <i class="fas fa-check"></i>
                            </span>
                            } @else if (ans.userAnswer === 'Skipped' || ans.userAnswer === 'Time Expired' ||
                            !ans.userAnswer) {
                            <span
                                class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-50 text-slate-400 font-semibold text-xs"
                                title="Unanswered">
                                -
                            </span>
                            } @else {
                            <span
                                class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-50 text-rose-600 font-bold text-xs"
                                title="Incorrect">
                                <i class="fas fa-times"></i>
                            </span>
                            }
                        </td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
    </div>
    }

    <span class="flex w-full items-center text-start text-md font-semibold text-primary-400 mt-4">My Attempts</span>
    @for (item of attempts; track item.id) {
    <div [routerLink]="['../active']" [queryParams]="{ attemptId: item.id }" [replaceUrl]="true"
        class="bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-gray-100">
        <div class="flex flex-col gap-1">
            <span class="text-xs text-primary-300">{{ item.modified | moment : 'Do MMM YYYY' }}</span>
            <span class="text-sm font-semibold text-primary-500">{{ item.title }}</span>
        </div>
        <div class="flex flex-col gap-2">
            @if (parent.quiz?.enableScoring !== false) {
            <div class="flex flex-row justify-end gap-2">
                <span class="text-xs flex items-end text-primary-300">Scored: </span>
                <span class="text-md flex items-end font-bold text-primary-500">{{ item.userScore ?? 0 }}</span>
            </div>
            }
            <div class="flex flex-row gap-2">
                <span class="text-xs text-primary-300">Qs Attempted: </span>
                <span class="text-xs text-primary-500">
                    {{ (item.completed ? (item.questions?.length ?? 0) : (item.currentIndex ?? 0)) }}/{{
                    item.questions?.length ?? '-' }}
                </span>
            </div>
        </div>
    </div>
    }
    @if (loadingAttempts()) {
    <div class="flex flex-row w-full items-center justify-center">
        <div class="animate-pulse min-h-14 w-full bg-gray-200 rounded-md"></div>
    </div>
    }
</div>
}